import React, { useState, useRef } from 'react';
import {
  Box,
  IconButton,
  Typography,
  alpha,
  useTheme,
  Tooltip,
  Avatar,
  Divider,
  Menu,
  MenuItem,
  useMediaQuery,
} from '@mui/material';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Dashboard as DashboardIcon,
  Assessment as AssessmentIcon,
  MenuBook as MenuBookIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Menu as MenuIcon,
  Article as DocumentIcon,
  People as PeopleIcon,
  Category as CategoryIcon,
  Security as SecurityIcon,
  History as HistoryIcon,
  BrandingWatermark as BrandingIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';

const buttonVariants = {
  initial: {
    gap: 0,
    paddingLeft: 8,
    paddingRight: 8,
  },
  animate: (isSelected) => ({
    gap: isSelected ? 8 : 0,
    paddingLeft: isSelected ? 16 : 8,
    paddingRight: isSelected ? 16 : 8,
  }),
};

const textVariants = {
  initial: { width: 0, opacity: 0 },
  animate: { width: 'auto', opacity: 1 },
  exit: { width: 0, opacity: 0 },
};

const transition = { delay: 0.1, type: 'spring', bounce: 0, duration: 0.6 };

const ExpandableNav = ({ user, onUserMenuOpen, onBeforeNavClick }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const [selected, setSelected] = useState(null);
  const navRef = useRef(null);
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState(null);
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const getProfileImage = (user) => {
    if (!user) return null;
    if (user.providerData?.[0]?.photoURL) {
      return user.providerData[0].photoURL;
    }
    return user.photoURL;
  };

  const handleMobileMenuOpen = (event) => {
    setMobileMenuAnchor(event.currentTarget);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
  };

  const handleMobileNavigation = (path) => {
    handleMobileMenuClose();
    // #region agent log
    try{const _l=JSON.parse(localStorage.getItem('_dbg_nav')||'[]');_l.push({t:Date.now(),src:'mobileNavClick',tabPath:path,curPath:location.pathname,same:path===location.pathname,willNav:path===location.pathname?'/app':path});localStorage.setItem('_dbg_nav',JSON.stringify(_l.slice(-50)));}catch(e){}
    // #endregion
    onBeforeNavClick?.();
    if (path === location.pathname) {
      navigate('/app');
    } else {
      navigate(path);
    }
  };

  const tabs = [
    { title: 'Hotel View', icon: BusinessIcon, path: '/app' },
    { title: 'Dashboard', icon: DashboardIcon, path: '/app/dashboard' },
    ...(user?.role === 'admin' || user?.role === 'SOS' ? [
      { title: 'Documentation', icon: DocumentIcon, path: '/app/documentation' },
      { title: 'Handbooks', icon: MenuBookIcon, path: '/app/handbooks' },
      { title: 'Strawberry Contacts', icon: PeopleIcon, path: '/app/contacts' },
      { title: 'Weekly Statistics', icon: AssessmentIcon, path: '/app/weekly-statistics' },
    ] : []),
    { type: 'separator' },
    { 
      title: 'Profile', 
      icon: PersonIcon, 
      isProfile: true,
      onClick: onUserMenuOpen,
      emoji: user?.emoji
    },
  ];

  const handleSelect = (event, tab) => {
    if (tab.isProfile) {
      tab.onClick(event);
      return;
    }
    // #region agent log
    try{const _l=JSON.parse(localStorage.getItem('_dbg_nav')||'[]');_l.push({t:Date.now(),src:'navClick',tabPath:tab.path,curPath:location.pathname,same:tab.path===location.pathname,willNav:tab.path===location.pathname?'/app':tab.path});localStorage.setItem('_dbg_nav',JSON.stringify(_l.slice(-50)));}catch(e){}
    // #endregion
    onBeforeNavClick?.();
    if (tab.path === location.pathname) {
      navigate('/app');
    } else {
      navigate(tab.path);
    }
    setSelected(null);
  };

  const Separator = () => (
    <Box
      sx={{
        mx: 0.25,
        height: 24,
        width: 1.2,
        bgcolor: 'divider',
        opacity: 0.5,
      }}
      aria-hidden="true"
    />
  );

  const renderMobileMenu = () => (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center',
      gap: 1
    }}>
      <IconButton
        onClick={handleMobileMenuOpen}
        sx={{
          color: 'text.primary',
          '&:hover': {
            backgroundColor: alpha(theme.palette.primary.main, 0.08),
          },
        }}
      >
        <MenuIcon />
      </IconButton>
      <Menu
        anchorEl={mobileMenuAnchor}
        open={Boolean(mobileMenuAnchor)}
        onClose={handleMobileMenuClose}
        PaperProps={{
          sx: {
            mt: 1.5,
            borderRadius: 2,
            minWidth: 200,
            boxShadow: theme => `0 4px 20px ${alpha(theme.palette.common.black, 0.1)}`,
          }
        }}
      >
        {tabs.map((tab, index) => {
          if (tab.type === 'separator') {
            return <Divider key={`separator-${index}`} sx={{ my: 1 }} />;
          }

          const Icon = tab.icon;
          const isSelected = location.pathname === tab.path;

          return (
            <MenuItem
              key={tab.title}
              onClick={() => tab.isProfile ? onUserMenuOpen() : handleMobileNavigation(tab.path)}
              sx={{
                py: 1.5,
                px: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                color: isSelected ? 'primary.main' : 'text.primary',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              {tab.isProfile ? (
                <Avatar 
                  src={getProfileImage(user)}
                  alt={user?.displayName || user?.email}
                  sx={{ 
                    width: 24,
                    height: 24,
                    color: 'primary.main',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                  }}
                >
                  {!getProfileImage(user) && (user?.displayName?.charAt(0) || user?.email?.charAt(0) || 'G')}
                </Avatar>
              ) : (
                <Icon sx={{ fontSize: 20 }} />
              )}
              <Typography variant="body2" sx={{ fontWeight: isSelected ? 500 : 400 }}>
                {tab.title}
              </Typography>
            </MenuItem>
          );
        })}
        
        {/* Admin Tools Section */}
        {user?.role === 'admin' && (
          <>
            <Divider sx={{ 
              my: 1,
              borderColor: theme => alpha(theme.palette.divider, 0.08),
            }} />
            
            <Box sx={{ px: 2, py: 1 }}>
              <Typography
                variant="caption"
                sx={{
                  color: 'text.secondary',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                  fontFamily: 'StrawberrySansText'
                }}
              >
                Admin Tools
              </Typography>
            </Box>

            <MenuItem 
              onClick={() => handleMobileNavigation('/app/category-management')}
              sx={{
                py: 1.5,
                px: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                color: location.pathname === '/app/category-management' ? 'primary.main' : 'text.primary',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              <CategoryIcon sx={{ fontSize: 20 }} />
              <Typography variant="body2" sx={{ fontWeight: location.pathname === '/app/category-management' ? 500 : 400 }}>
                Category Management
              </Typography>
            </MenuItem>

            <MenuItem 
              onClick={() => handleMobileNavigation('/app/chain-management')}
              sx={{
                py: 1.5,
                px: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                color: location.pathname === '/app/chain-management' ? 'primary.main' : 'text.primary',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              <BrandingIcon sx={{ fontSize: 20 }} />
              <Typography variant="body2" sx={{ fontWeight: location.pathname === '/app/chain-management' ? 500 : 400 }}>
                Chain Management
              </Typography>
            </MenuItem>

            <MenuItem 
              onClick={() => handleMobileNavigation('/app/user-management')}
              sx={{
                py: 1.5,
                px: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                color: location.pathname === '/app/user-management' ? 'primary.main' : 'text.primary',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              <SecurityIcon sx={{ fontSize: 20 }} />
              <Typography variant="body2" sx={{ fontWeight: location.pathname === '/app/user-management' ? 500 : 400 }}>
                User Management
              </Typography>
            </MenuItem>

            <MenuItem 
              onClick={() => handleMobileNavigation('/hotels')}
              sx={{
                py: 1.5,
                px: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                color: location.pathname === '/hotels' ? 'primary.main' : 'text.primary',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              <BusinessIcon sx={{ fontSize: 20 }} />
              <Typography variant="body2" sx={{ fontWeight: location.pathname === '/hotels' ? 500 : 400 }}>
                Hotel Management
              </Typography>
            </MenuItem>

            <MenuItem 
              onClick={() => handleMobileNavigation('/app/activity-logs')}
              sx={{
                py: 1.5,
                px: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                color: location.pathname === '/app/activity-logs' ? 'primary.main' : 'text.primary',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              <HistoryIcon sx={{ fontSize: 20 }} />
              <Typography variant="body2" sx={{ fontWeight: location.pathname === '/app/activity-logs' ? 500 : 400 }}>
                Activity Logs
              </Typography>
            </MenuItem>

            <MenuItem 
              onClick={() => handleMobileNavigation('/app/force-refresh')}
              sx={{
                py: 1.5,
                px: 2,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
                color: location.pathname === '/app/force-refresh' ? 'primary.main' : 'text.primary',
                '&:hover': {
                  backgroundColor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              <RefreshIcon sx={{ fontSize: 20 }} />
              <Typography variant="body2" sx={{ fontWeight: location.pathname === '/app/force-refresh' ? 500 : 400 }}>
                Force Refresh
              </Typography>
            </MenuItem>
          </>
        )}
      </Menu>
    </Box>
  );

  const renderDesktopNav = () => (
    <Box
      ref={navRef}
      sx={{
        display: 'flex',
        flexWrap: 'nowrap',
        alignItems: 'center',
        gap: 0.25,
        borderRadius: '32px',
        border: 'none',
        bgcolor: 'background.paper',
        p: 0.5,
        pl: 0.8,
        boxShadow: theme => `0 2px 8px ${alpha(theme.palette.common.black, 0.03)}`,
        transition: 'all 0.3s ease',
        '&:hover': {
          boxShadow: theme => `0 4px 12px ${alpha(theme.palette.common.black, 0.06)}`,
        },
        overflowX: 'auto',
        '&::-webkit-scrollbar': {
          display: 'none'
        },
        msOverflowStyle: 'none',
        scrollbarWidth: 'none',
      }}
    >
      {tabs.map((tab, index) => {
        if (tab.type === 'separator') {
          return <Separator key={`separator-${index}`} />;
        }

        const Icon = tab.icon;
        const isSelected = selected === index || location.pathname === tab.path;

        return (
          <Tooltip key={tab.title} title={!isSelected ? tab.title : ''} placement="top">
            <motion.div
              variants={buttonVariants}
              initial={false}
              animate="animate"
              custom={isSelected}
              onClick={(e) => handleSelect(e, tab)}
              transition={transition}
              style={{
                position: 'relative',
                display: 'flex',
                alignItems: 'center',
                borderRadius: '24px',
                padding: '12px 20px',
                cursor: 'pointer',
                backgroundColor: isSelected ? alpha(theme.palette.primary.main, 0.08) : 'transparent',
                color: isSelected ? theme.palette.primary.main : theme.palette.text.primary,
                boxShadow: isSelected 
                  ? `0 0 0 0px ${alpha(theme.palette.primary.main, 0.1)}, 0 0px 0px ${alpha(theme.palette.primary.main, 0.15)}, inset 0 1px 0 ${alpha(theme.palette.primary.main, 0.1)}`
                  : 'none',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                '&:hover': {
                  backgroundColor: isSelected 
                    ? alpha(theme.palette.primary.main, 0.08)
                    : alpha(theme.palette.primary.main, 0.06),
                  color: theme.palette.primary.main,
                  boxShadow: isSelected
                    ? `0 0 0 1px ${alpha(theme.palette.primary.main, 0.15)}, 0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}, inset 0 1px 0 ${alpha(theme.palette.primary.main, 0.15)}`
                    : `0 2px 6px ${alpha(theme.palette.primary.main, 0.1)}`
                }
              }}
            >
              {tab.isProfile ? (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  gap: 1,
                  height: '100%',
                  justifyContent: 'center'
                }}>
                  <Avatar 
                    src={getProfileImage(user)}
                    alt={user?.displayName || user?.email}
                    sx={{ 
                      width: 26,
                      height: 26,
                      color: 'primary.main',
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      fontFamily: 'StrawberrySansDisplay',
                      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      border: '2px solid',
                      borderColor: 'transparent',
                      '&:hover': {
                        transform: 'scale(1.05)',
                        boxShadow: theme => `0 0 0 2px ${alpha(theme.palette.primary.main, 0.2)}`
                      }
                    }}
                  >
                    {!getProfileImage(user) && (user?.displayName?.charAt(0) || user?.email?.charAt(0) || 'G')}
                  </Avatar>
                  {tab.emoji && (
                    <Typography
                      sx={{
                        fontSize: '1.1rem',
                        filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.2))',
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        color: 'text.primary',
                        opacity: 1
                      }}
                    >
                      {tab.emoji}
                    </Typography>
                  )}
                </Box>
              ) : (
                <Icon sx={{ fontSize: 22 }} />
              )}
              <AnimatePresence initial={false}>
                {isSelected && (
                  <motion.div
                    variants={textVariants}
                    initial="initial"
                    animate="animate"
                    exit="exit"
                    transition={transition}
                    style={{ overflow: 'hidden' }}
                  >
                    <Typography
                      variant="body2"
                      sx={{
                        ml: 1,
                        fontWeight: 500,
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {tab.title}
                    </Typography>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          </Tooltip>
        );
      })}
    </Box>
  );

  return (
    <Box sx={{ 
      display: 'flex', 
      alignItems: 'center',
      width: '100%',
      justifyContent: isMobile ? 'flex-start' : 'center'
    }}>
      {isMobile ? renderMobileMenu() : renderDesktopNav()}
    </Box>
  );
};

export default ExpandableNav; 